import React, { useState } from 'react'
import {
  makeStyles,
  Card,
  ResultsTable,
  TableRow,
  Grid,
  Typography,
  useDialogs,
  Button,
  EstimatorAvatar,
  Tooltip,
  EventPreviewPopper,
  useUsers,
  useClientOptions,
  ProductionEventIcon,
  CancelledEventIcon,
  PastEventIcon,
  UpcomingEventIcon
} from '@paintscout/ui'
import type { MuiTheme as Theme, StyleClasses } from '@paintscout/ui'
import { useCancelEventFlow } from 'src/hooks'
import moment from 'moment'
import AddIcon from '@material-ui/icons/Add'
import type { CalendarEvent } from 'paintscout'
import { getActivityColor, removeAuth0Prefix } from '@paintscout/util'
import { getCrews, startCase, translateEventType } from '@paintscout/util/builder'
import { getTimezone } from '@paintscout/util/src/builder'
import { useLocation } from 'react-router-dom'
import ProductionEventDialog from 'src/components/dialogs/EventDialog/ProductionEventDialog'
import { EventDialog } from '../../dialogs'

const useStyles = makeStyles<Theme, EventTableProps>(
  (theme) => ({
    root: {},
    card: {
      border: 'none'
    },
    titleContainer: {
      paddingLeft: theme.spacing(2),
      paddingTop: theme.spacing(1),
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      width: '100%'
    },
    eventDocItem: {
      display: 'flex',
      justifyContent: 'flex-start',
      alignItems: 'center',
      padding: theme.spacing(2),
      '&:hover': {
        cursor: 'pointer',
        backgroundColor: theme.paintscout.palette.brand.Neutral.P96
      },
      '&:not(:last-child)': {
        borderBottom: `2px solid ${theme.paintscout.palette.brand.Neutral.P90}`
      }
    },
    eventIcon: {
      marginRight: theme.spacing(1),
      marginBottom: theme.spacing(1.5)
    }
  }),
  { name: 'EventTable' }
)

export interface EventTableProps {
  classes?: StyleClasses<typeof useStyles>
  events: CalendarEvent[]
  loading?: boolean
  totalRows: number
  rowsPerPage: number
  pages: number
  page: number
  title?: string
  handleCreate?: () => any
  onPageChange?: (page: number) => any
  onEdit: (event: CalendarEvent, template?: string) => any
  disablePagination?: boolean
}

function EventTable(props: EventTableProps) {
  const classes = useStyles(props)
  const {
    events,
    loading,
    rowsPerPage,
    onPageChange,
    page,
    totalRows,
    title,
    handleCreate,
    onEdit,
    disablePagination
  } = props
  const { openDialog, dismissDialog } = useDialogs()
  const cancelEventFlow = useCancelEventFlow()
  const { users, userPictures } = useUsers()
  const { options } = useClientOptions()
  const timezone = getTimezone({ options })
  const { pathname } = useLocation()

  const [anchorEl, setAnchorEl] = useState(null)
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null)

  const handleRowClick = (e: React.MouseEvent<HTMLTableRowElement>, row: CalendarEvent) => {
    if ((e.target as HTMLElement).classList?.contains('disable-click')) {
      return
    }
    if (selectedEvent && selectedEvent.id === row.id) {
      setAnchorEl(null)
      setSelectedEvent(null)
      return
    }
    setAnchorEl(e.currentTarget)
    setSelectedEvent(row)
  }

  const handleCancelEvent = (event: CalendarEvent) =>
    cancelEventFlow(event, (evt, template) => {
      onEdit(evt, template)
      setAnchorEl(null)
    })

  const handleEditEvent = () => {
    if (selectedEvent.production) {
      openDialog(ProductionEventDialog, {
        event: selectedEvent,
        onCancelEvent: (event: CalendarEvent) => {
          handleCancelEvent(event)
          dismissDialog()
        },
        onConfirm: (event: CalendarEvent) => {
          onEdit(event)
          dismissDialog()
        }
      })
    } else {
      openDialog(EventDialog, {
        event: selectedEvent,
        onDelete: (event: CalendarEvent, eventTemplate: string) => {
          onEdit(event, eventTemplate)
          dismissDialog()
        },
        onConfirm: (event: CalendarEvent, eventTemplate: string) => {
          onEdit(event, eventTemplate)
          dismissDialog()
        }
      })
    }

    setAnchorEl(null)
    setSelectedEvent(null)
  }

  const renderRow = (row: CalendarEvent) => {
    const { eventType, canceled, deal, start, end, production } = row

    const dealName = deal?.name
    const variant: 'upcoming' | 'past' | 'canceled' | 'production' = production
      ? 'production'
      : canceled
        ? 'canceled'
        : moment(row.start).isAfter(moment())
          ? 'upcoming'
          : 'past'

    const isAllDay =
      moment(start).get('hour') === 0 && moment(end).get('hour') === 0 && moment(end).diff(moment(start), 'days') === 1
    const spansMultipleDays = moment(end).diff(moment(start), 'days') > 1
    const timeSection = isAllDay
      ? moment(start).tz(timezone).format('MMMM D')
      : spansMultipleDays
        ? moment(start).tz(timezone).year() === moment(end).tz(timezone).year()
          ? production
            ? `${moment(start).tz(timezone).format('MMM D')} - ${moment(end).tz(timezone).format('MMM D')}`
            : `${moment(start).tz(timezone).format('MMM D, h:mma')} - ${moment(end).tz(timezone).format('MMM D, h:mma')}`
          : production
            ? `${moment(start).tz(timezone).format('MMM D, YYYY')} - ${moment(end).tz(timezone).format('MMM D, YYYY')}`
            : `${moment(start).tz(timezone).format('MMM D, YYYY, h:mma')} - ${moment(end).tz(timezone).format('MMM D, YYYY, h:mma')}`
        : `${moment(start).tz(timezone).format('MMMM D, h:mma')}`

    const dealSection = pathname.includes('deals') || !dealName ? '' : dealName

    const matchingCrew = row.crew
      ? getCrews({ options, includeTrashed: true }).find((crew) => crew._id === row.crew)
      : null

    const subtitle = [timeSection, dealSection, matchingCrew?.name].filter(Boolean).join(' · ')

    return (
      <TableRow key={row.id} data-testid={`event-${row.id}`} onClick={(e) => handleRowClick(e, row)}>
        <Grid container className={classes.eventDocItem}>
          <Grid item>
            <Tooltip content={startCase(variant)} hideIcon>
              <>
                {variant === 'production' && <ProductionEventIcon className={classes.eventIcon} />}
                {variant === 'upcoming' && <UpcomingEventIcon className={classes.eventIcon} />}
                {variant === 'past' && <PastEventIcon className={classes.eventIcon} />}
                {variant === 'canceled' && <CancelledEventIcon className={classes.eventIcon} />}
              </>
            </Tooltip>
          </Grid>
          <Grid item xs style={{ paddingRight: '8px' }}>
            <Typography variant={'body1'}>{translateEventType({ eventType, options })}</Typography>
            <Typography variant={'subtitle2'} color="textSecondary">
              {subtitle}
            </Typography>
          </Grid>
          <Grid item>
            <EstimatorAvatar
              userPictures={userPictures}
              owner={
                matchingCrew
                  ? {
                      id: removeAuth0Prefix(matchingCrew?.projectManager?.id),
                      firstName: matchingCrew?.projectManager?.firstName,
                      lastName: matchingCrew?.projectManager?.lastName
                    }
                  : {
                      id: row.estimator.id,
                      firstName: (row.estimator.name ?? '').split(' ')[0],
                      lastName: (row.estimator.name ?? '').split(' ')[1]
                    }
              }
              width="36px"
              height="36px"
              fontSize="0.9rem"
              avatarColor={getActivityColor(row.estimator.color)}
            />
          </Grid>
        </Grid>
      </TableRow>
    )
  }

  function handlePageChange(page: number) {
    if (onPageChange) {
      onPageChange(page)
    }
  }

  const rows = events
  return (
    <Card className={classes.card} data-testid={'quote-list'}>
      {title && (
        <div className={classes.titleContainer}>
          <Typography variant="h3">{title}</Typography>
          {handleCreate && (
            <Button
              variant="hoverUnderline"
              color="primary"
              icon={AddIcon}
              onClick={handleCreate}
              className="tour-event-table-new-button"
            >
              New
            </Button>
          )}
        </div>
      )}
      <ResultsTable
        classes={{
          table: classes.table,
          tableHead: classes.tableHead
        }}
        shimHeight={'36px'}
        columns={<></>}
        renderRow={renderRow}
        rows={rows}
        rowsPerPage={rowsPerPage}
        loading={loading}
        page={page}
        totalRows={totalRows}
        onPageChange={handlePageChange}
        disablePagination={disablePagination}
        noResultsMessage={'No events found.'}
      />
      <EventPreviewPopper
        event={selectedEvent}
        setAnchorEl={setAnchorEl}
        anchorEl={anchorEl}
        users={users}
        onClose={() => {
          setAnchorEl(null)
          setSelectedEvent(null)
        }}
        onEditEvent={handleEditEvent}
        onCancelEvent={handleCancelEvent}
      />
    </Card>
  )
}

export default EventTable
